import { useState, useCallback } from 'react'
import { useLocation } from 'react-router-dom'
import { useAppDispatch } from 'hooks/reduxHooks'
import { pushWithRegion, replaceWithRegion } from 'actions/NavigationActions'
import { CHAT_TRIP_PLANNER_NEW_PATH, CHAT_TRIP_PLANNER_PATH, HOTEL_SEARCH_CHAT_PROMPT_QUERY_PARAM } from 'tripPlanner/config'
import { STAGED_CHAT_PROMPT_QUERY_PARAM } from 'tripPlanner/utils/stagedChatPrompt'

export const CHAT_TRIP_PLANNER_ROUTE = CHAT_TRIP_PLANNER_PATH
export const NEW_CHAT_ROUTE_SEGMENT = 'new' as const

export type ChatNavigationOptions = {
  shouldUpdateUrl?: boolean
  shouldReplaceUrl?: boolean
  search?: string
}

interface UseChatNavigationArgs {
  routeChatId: string | undefined
}

interface UseChatNavigationReturn {
  chatId: number | undefined
  setChatId: (nextChatId: number | undefined, options?: ChatNavigationOptions) => void
  setChatIdForStreaming: (nextChatId: number | undefined) => void
  isNewChatRoute: boolean
  isLandingRoute: boolean
  hasRouteChatId: boolean
  existingRouteChatId: number | undefined
  navigateToNewChatRoute: () => void
}

export function useChatNavigation({
  routeChatId,
}: UseChatNavigationArgs): UseChatNavigationReturn {
  const [chatId, setChatIdState] = useState<number | undefined>(undefined)
  const dispatch = useAppDispatch()
  const { search } = useLocation()

  const isNewChatRoute = routeChatId === NEW_CHAT_ROUTE_SEGMENT
  const normalizedRouteChatId = !isNewChatRoute && routeChatId ? Number(routeChatId) : undefined
  const hasRouteChatId = Number.isFinite(normalizedRouteChatId)
  const isLandingRoute = !routeChatId
  const existingRouteChatId = hasRouteChatId ? normalizedRouteChatId : undefined
  const searchParams = new URLSearchParams(search)
  const shouldReplaceNewChatUrl = searchParams.has(STAGED_CHAT_PROMPT_QUERY_PARAM) ||
    searchParams.has(HOTEL_SEARCH_CHAT_PROMPT_QUERY_PARAM)

  const navigateToChatRoute = useCallback((nextChatId?: number, options?: ChatNavigationOptions) => {
    const nextPath = typeof nextChatId === 'number'
      ? `${CHAT_TRIP_PLANNER_ROUTE}/${nextChatId}`
      : CHAT_TRIP_PLANNER_ROUTE
    if (options?.shouldReplaceUrl) {
      dispatch(replaceWithRegion(nextPath, options.search))
      return
    }
    dispatch(pushWithRegion(nextPath, options?.search))
  }, [dispatch])

  const navigateToNewChatRoute = useCallback(() => {
    dispatch(pushWithRegion(CHAT_TRIP_PLANNER_NEW_PATH))
  }, [dispatch])

  const updateChatId = useCallback((nextChatId: number | undefined, options?: ChatNavigationOptions) => {
    setChatIdState(nextChatId)
    if (options?.shouldUpdateUrl === false) {
      return
    }
    navigateToChatRoute(nextChatId, options)
  }, [navigateToChatRoute])

  const setChatIdForStreaming = useCallback((nextChatId: number | undefined) => {
    updateChatId(nextChatId, {
      shouldReplaceUrl: isNewChatRoute && shouldReplaceNewChatUrl && typeof nextChatId === 'number',
    })
  }, [isNewChatRoute, shouldReplaceNewChatUrl, updateChatId])

  return {
    chatId,
    setChatId: updateChatId,
    setChatIdForStreaming,
    isNewChatRoute,
    isLandingRoute,
    hasRouteChatId,
    existingRouteChatId,
    navigateToNewChatRoute,
  }
}
